import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../auth/AuthContext/AuthContext";
import { connectSocket, getSocket } from "../../auth/AuthContext/socketManager";

interface OnlineSession {
  sessionId: string;
  email: string;
  connectedAt: string;
  platform: string | null;
  mobile: boolean | null;
  lastServiceMessage: {
    id: string;
    sentAt: string;
    readAt: string | null;
  } | null;
}

interface SendResult {
  ok: boolean;
  delivered?: number;
  error?: string;
}

const UPDATE_EXIT_MESSAGE = "Si prega di uscire dall'applicazione. A breve verrà eseguito un aggiornamento software.";

const OnlineSessions = () => {
  const { users } = useAuth();
  const [sessions, setSessions] = useState<OnlineSession[]>([]);
  const [connected, setConnected] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sendToAll, setSendToAll] = useState(false);
  const [message, setMessage] = useState("");
  const [useUpdatePreset, setUseUpdatePreset] = useState(false);
  const [savedDraft, setSavedDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ severity: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    void connectSocket();
    const socket = getSocket();
    if (!socket) return;

    const subscribe = () => {
      setConnected(true);
      socket.emit("online-sessions:subscribe");
    };
    const handleDisconnect = () => {
      setConnected(false);
      setSessions([]);
    };
    const handleUpdate = (next: OnlineSession[]) => {
      if (Array.isArray(next)) {
        setSessions(next);
        const onlineIds = new Set(next.map((session) => session.sessionId));
        setSelectedIds((current) => current.filter((id) => onlineIds.has(id)));
      }
    };

    socket.on("connect", subscribe);
    socket.on("disconnect", handleDisconnect);
    socket.on("online-sessions:update", handleUpdate);
    if (socket.connected) subscribe();

    return () => {
      socket.emit("online-sessions:unsubscribe");
      socket.off("connect", subscribe);
      socket.off("disconnect", handleDisconnect);
      socket.off("online-sessions:update", handleUpdate);
    };
  }, []);

  const namesByEmail = useMemo(() => new Map(
    users.map((user) => [
      user.email.toLowerCase(),
      `${user.firstName || ""} ${user.lastName || ""}`.trim(),
    ])
  ), [users]);

  const sortedSessions = useMemo(() => [...sessions].sort(
    (a, b) => Date.parse(b.connectedAt) - Date.parse(a.connectedAt)
  ), [sessions]);

  const toggleUpdatePreset = (checked: boolean) => {
    if (checked) {
      setSavedDraft(message);
      setMessage(UPDATE_EXIT_MESSAGE);
    } else {
      setMessage(savedDraft);
    }
    setUseUpdatePreset(checked);
  };

  const sendMessage = () => {
    const socket = getSocket();
    if (!socket?.connected) {
      setFeedback({ severity: "error", text: "Connessione non disponibile. Riprova quando il socket è connesso." });
      return;
    }
    setSending(true);
    setFeedback(null);
    socket.timeout(5000).emit("service-message:send", {
      message: message.trim(),
      all: sendToAll,
      sessionIds: sendToAll ? [] : selectedIds,
    }, (error: Error | null, result: SendResult) => {
      setSending(false);
      if (error || !result?.ok) {
        setFeedback({ severity: "error", text: result?.error || "Invio non confermato dal server. Riprova." });
        return;
      }
      setFeedback({ severity: "success", text: `Messaggio inviato a ${result.delivered ?? 0} sessioni connesse.` });
      setComposerOpen(false);
      setMessage("");
      setUseUpdatePreset(false);
      setSavedDraft("");
      setSendToAll(false);
      setSelectedIds([]);
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="h5" component="h1">Utenti online</Typography>
        <Chip label={`${sessions.length} sessioni`} size="small" color="primary" variant="outlined" />
        <Button variant="contained" sx={{ ml: { md: "auto" } }} onClick={() => { setFeedback(null); setComposerOpen(true); }}>
          Nuovo messaggio
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ogni riga rappresenta una connessione aperta, anche quando lo stesso utente usa più schede o dispositivi.
        L’ora indica la connessione al server.
      </Typography>

      {!connected && <Alert severity="warning" sx={{ mb: 2 }}>Connessione in tempo reale non disponibile.</Alert>}
      {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.text}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Sessioni utenti online">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">Invia</TableCell>
              <TableCell>Utente</TableCell>
              <TableCell>Ultimo avviso</TableCell>
              <TableCell>Ora connessione</TableCell>
              <TableCell>ID sessione Socket</TableCell>
              <TableCell>Piattaforma</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSessions.map((session) => (
              <TableRow key={session.sessionId}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(session.sessionId)}
                    onChange={(_, checked) => setSelectedIds((current) => checked
                      ? [...current, session.sessionId]
                      : current.filter((id) => id !== session.sessionId))}
                    inputProps={{ "aria-label": `Seleziona ${namesByEmail.get(session.email.toLowerCase()) || session.email || session.sessionId}` }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {namesByEmail.get(session.email.toLowerCase()) || session.email || "Utente non identificato"}
                  </Typography>
                  {namesByEmail.has(session.email.toLowerCase()) && (
                    <Typography variant="caption" color="text.secondary">{session.email}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {session.lastServiceMessage ? (
                    <>
                      <Chip
                        label={session.lastServiceMessage.readAt ? "Letto" : "In attesa"}
                        size="small"
                        color={session.lastServiceMessage.readAt ? "success" : "warning"}
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        {session.lastServiceMessage.readAt
                          ? `Confermato: ${new Date(session.lastServiceMessage.readAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}`
                          : `Inviato: ${new Date(session.lastServiceMessage.sentAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}`}
                      </Typography>
                    </>
                  ) : "—"}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {new Date(session.connectedAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" })}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                  {session.sessionId}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{session.platform || "Non disponibile"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {session.mobile == null ? "Tipo di dispositivo non disponibile" : session.mobile ? "Mobile / tablet" : "Non mobile"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {sortedSessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {connected ? "Nessuna sessione attiva." : "In attesa della connessione."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer anchor="right" open={composerOpen} onClose={() => { if (!sending) setComposerOpen(false); }}>
        <Box sx={{ width: { xs: "100vw", sm: 440 }, p: 3, display: "flex", flexDirection: "column", gap: 2 }} role="form" aria-label="Messaggio di servizio">
          <Typography variant="h6">Messaggio di servizio</Typography>
          <Typography variant="body2" color="text.secondary">
            {sendToAll ? `Destinatari: tutte le ${sessions.length} sessioni online.` : `Destinatari selezionati: ${selectedIds.length} sessioni.`}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={sendToAll} onChange={(_, checked) => setSendToAll(checked)} />}
            label="Invia a tutti gli utenti online"
          />
          <FormControlLabel
            control={<Checkbox checked={useUpdatePreset} disabled={sending} onChange={(_, checked) => toggleUpdatePreset(checked)} />}
            label="Inserisci il messaggio preimpostato per l'uscita prima dell'aggiornamento"
          />
          <TextField
            label="Messaggio"
            multiline
            minRows={5}
            maxRows={12}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              if (useUpdatePreset) setUseUpdatePreset(false);
            }}
            inputProps={{ maxLength: 1000 }}
            helperText={`${message.length}/1000 caratteri`}
            fullWidth
            autoFocus
          />
          {feedback?.severity === "error" && <Alert severity="error">{feedback.text}</Alert>}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setComposerOpen(false)} disabled={sending}>Annulla</Button>
            <Button variant="contained" onClick={sendMessage} disabled={sending || !connected || !message.trim() || (!sendToAll && selectedIds.length === 0)}>
              {sending ? "Invio…" : "Invia"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default OnlineSessions;
